<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PipelineWebhookModal from './PipelineWebhookModal.vue';
import ConfirmModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import {
  formatLocalizedDateInTimezone,
  resolveReportingTimezone,
} from '../utils/localeFormatting';

defineProps({
  pipelines: {
    type: Array,
    default: () => [],
  },
});

const store = useStore();
const { showAlert } = useAlert();
const { t, locale } = useI18n();

const showModal = ref(false);
const selectedWebhook = ref(null);
const testingId = ref(null);
const deletingId = ref(null);
const loadError = ref(false);
const deleteConfirmation = ref(null);
const pendingDelete = ref(null);
const deleteOperationToken = ref(null);
const testOperationToken = ref(null);
let accountGeneration = 0;
let fetchGeneration = 0;

const webhooks = computed(() => store.getters['pipelineWebhooks/allWebhooks']);
const uiFlags = computed(() => store.getters['pipelineWebhooks/uiFlags']);
const currentAccountId = computed(
  () =>
    store.getters.getCurrentAccountId ??
    store.getters.getCurrentAccount?.id ??
    null
);
const normalizedAccountId = () =>
  currentAccountId.value === undefined || currentAccountId.value === null
    ? null
    : String(currentAccountId.value);
const captureAccountContext = () => ({
  accountId: normalizedAccountId(),
  generation: accountGeneration,
});
const accountContextIsCurrent = context =>
  context.generation === accountGeneration &&
  context.accountId === normalizedAccountId();

const reportingTimezone = computed(() => resolveReportingTimezone(store));

const fetchWebhooks = async () => {
  const context = captureAccountContext();
  fetchGeneration += 1;
  const requestGeneration = fetchGeneration;
  loadError.value = false;
  try {
    await store.dispatch('pipelineWebhooks/get', {
      accountId: context.accountId,
      generation: requestGeneration,
    });
  } catch {
    if (
      accountContextIsCurrent(context) &&
      requestGeneration === fetchGeneration
    ) {
      loadError.value = true;
    }
  }
};

onMounted(fetchWebhooks);

watch(
  currentAccountId,
  (accountId, previousAccountId) => {
    if (String(accountId ?? '') === String(previousAccountId ?? '')) return;

    accountGeneration += 1;
    fetchGeneration += 1;
    loadError.value = false;
    showModal.value = false;
    selectedWebhook.value = null;
    pendingDelete.value = null;
    deleteOperationToken.value = null;
    testOperationToken.value = null;
    deletingId.value = null;
    testingId.value = null;
    store.dispatch('pipelineWebhooks/reset');
    fetchWebhooks();
  },
  { flush: 'sync' }
);

const EVENT_META = {
  pipeline_card_created: {
    classes: 'bg-success-subtle text-success',
    icon: 'i-lucide-plus-circle',
  },
  pipeline_card_updated: {
    classes: 'bg-n-blue-3 text-n-blue-11',
    icon: 'i-lucide-pencil-line',
  },
  pipeline_card_deleted: {
    classes: 'bg-n-red-3 text-danger',
    icon: 'i-lucide-trash-2',
  },
  pipeline_card_stage_changed: {
    classes: 'bg-n-yellow-3 text-n-yellow-11',
    icon: 'i-lucide-arrow-right-circle',
  },
  pipeline_card_won: {
    classes: 'bg-success-subtle text-success',
    icon: 'i-lucide-trophy',
  },
  pipeline_card_lost: {
    classes: 'bg-n-red-3 text-danger',
    icon: 'i-lucide-circle-x',
  },
  pipeline_card_owner_changed: {
    classes: 'bg-n-purple-3 text-n-purple-11',
    icon: 'i-lucide-user-round-cog',
  },
  pipeline_card_sla_exceeded: {
    classes: 'bg-n-amber-3 text-n-amber-11',
    icon: 'i-lucide-alarm-clock',
  },
};

const getEventMeta = event => ({
  ...(EVENT_META[event] || {
    classes: 'bg-n-alpha-2 text-n-slate-11',
    icon: 'i-lucide-zap',
  }),
  label: EVENT_META[event]
    ? t(`PIPELINE_WEBHOOK.FORM.EVENTS.${event}.LABEL`)
    : event,
});

const formatDate = dateStr => {
  return formatLocalizedDateInTimezone(dateStr, {
    locale: locale.value,
    timeZone: reportingTimezone.value,
    dateStyle: 'short',
    timeStyle: 'short',
  });
};

const openCreateModal = () => {
  selectedWebhook.value = null;
  showModal.value = true;
};

const openEditModal = webhook => {
  selectedWebhook.value = webhook;
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedWebhook.value = null;
};

const handleSaved = () => {
  closeModal();
  fetchWebhooks();
};

const handleTest = async webhook => {
  if (testOperationToken.value !== null) return;
  const context = captureAccountContext();
  const token = Symbol('pipeline-webhook-test');
  testOperationToken.value = token;
  testingId.value = webhook.id;
  try {
    await store.dispatch('pipelineWebhooks/test', {
      id: webhook.id,
      accountId: context.accountId,
    });
    if (
      testOperationToken.value === token &&
      accountContextIsCurrent(context)
    ) {
      showAlert(t('PIPELINE_WEBHOOK.MESSAGES.TEST_SENT_SUCCESS'));
    }
  } catch {
    if (
      testOperationToken.value === token &&
      accountContextIsCurrent(context)
    ) {
      showAlert(t('PIPELINE_WEBHOOK.MESSAGES.TEST_FAILED'));
    }
  } finally {
    if (testOperationToken.value === token) {
      testOperationToken.value = null;
      testingId.value = null;
    }
  }
};

const handleDelete = async webhook => {
  if (deleteOperationToken.value !== null) return;
  const context = captureAccountContext();
  const token = Symbol('pipeline-webhook-delete');
  deleteOperationToken.value = token;
  pendingDelete.value = webhook;
  const confirmed = await deleteConfirmation.value.showConfirmation();
  if (
    deleteOperationToken.value !== token ||
    !accountContextIsCurrent(context)
  ) {
    return;
  }
  if (!confirmed) {
    deleteOperationToken.value = null;
    pendingDelete.value = null;
    return;
  }

  deletingId.value = webhook.id;
  try {
    await store.dispatch('pipelineWebhooks/delete', {
      id: webhook.id,
      accountId: context.accountId,
    });
    if (
      deleteOperationToken.value === token &&
      accountContextIsCurrent(context)
    ) {
      showAlert(t('PIPELINE_WEBHOOK.DELETE_SUCCESS'));
    }
  } catch {
    if (
      deleteOperationToken.value === token &&
      accountContextIsCurrent(context)
    ) {
      showAlert(t('PIPELINE_WEBHOOK.DELETE_ERROR'));
    }
  } finally {
    if (deleteOperationToken.value === token) {
      deleteOperationToken.value = null;
      deletingId.value = null;
      pendingDelete.value = null;
    }
  }
};
</script>

<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!-- Header -->
    <div
      class="flex flex-col items-stretch gap-3 border-b border-n-weak px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:pl-6 sm:pr-14"
    >
      <div class="flex items-center gap-3">
        <div
          class="w-8 h-8 rounded-lg bg-n-brand/10 flex items-center justify-center shrink-0"
        >
          <Icon icon="i-lucide-webhook" class="size-4 text-n-brand" />
        </div>
        <div>
          <h2 class="text-sm font-semibold text-n-slate-12 leading-none">
            {{ $t('PIPELINE_WEBHOOK.TITLE') }}
          </h2>
          <p class="text-xs text-n-slate-11 mt-0.5">
            {{ $t('PIPELINE_WEBHOOK.DESCRIPTION') }}
          </p>
        </div>
      </div>
      <Button size="md" class="w-full sm:w-auto" @click="openCreateModal">
        <template #icon>
          <Icon icon="i-lucide-plus" class="size-4" />
        </template>
        {{ $t('PIPELINE_WEBHOOK.CREATE_BUTTON') }}
      </Button>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto px-5 py-4">
      <!-- Loading -->
      <div
        v-if="uiFlags.isFetching"
        role="status"
        class="flex items-center justify-center gap-2 py-12"
      >
        <Icon
          icon="i-lucide-loader-circle"
          class="size-4 text-n-slate-11 motion-safe:animate-spin"
        />
        <span class="text-n-slate-11 text-sm">
          {{ $t('PIPELINE_WEBHOOK.LOADING') }}
        </span>
      </div>

      <div
        v-else-if="loadError"
        role="alert"
        class="flex flex-col items-center justify-center gap-3 rounded-xl border border-n-ruby-8 bg-n-ruby-3 px-4 py-10 text-center"
      >
        <Icon icon="i-lucide-circle-alert" class="size-6 text-n-ruby-11" />
        <p class="text-sm text-n-ruby-11">
          {{ $t('PIPELINE_WEBHOOK.LOAD_ERROR') }}
        </p>
        <Button variant="outline" color="ruby" size="md" @click="fetchWebhooks">
          {{ $t('PIPELINE_WEBHOOK.RETRY') }}
        </Button>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="webhooks.length === 0"
        class="flex flex-col items-center justify-center py-14 gap-4"
      >
        <div
          class="w-14 h-14 rounded-2xl bg-n-alpha-2 flex items-center justify-center"
        >
          <Icon icon="i-lucide-webhook" class="size-7 text-n-slate-10" />
        </div>
        <div class="text-center">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ $t('PIPELINE_WEBHOOK.EMPTY_STATE.TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-11 mt-1 max-w-xs leading-relaxed">
            {{ $t('PIPELINE_WEBHOOK.EMPTY_STATE.DESCRIPTION') }}
          </p>
        </div>
        <Button size="md" @click="openCreateModal">
          {{ $t('PIPELINE_WEBHOOK.EMPTY_STATE.CTA') }}
        </Button>
      </div>

      <!-- Webhook List -->
      <div v-else class="flex flex-col gap-2.5">
        <div
          v-for="webhook in webhooks"
          :key="webhook.id"
          class="group relative rounded-xl border bg-n-background transition-[border-color,box-shadow,opacity] duration-150 overflow-hidden"
          :class="
            webhook.active
              ? 'border-n-weak hover:border-n-strong hover:shadow-sm'
              : 'border-n-weaker opacity-70 hover:opacity-100'
          "
        >
          <!-- Active accent bar -->
          <div
            class="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-xl transition-colors"
            :class="webhook.active ? 'bg-success' : 'bg-n-weak'"
          />

          <div class="pl-5 pr-4 py-3.5">
            <div class="flex items-start justify-between gap-3">
              <!-- Main info -->
              <div class="flex-1 min-w-0">
                <!-- Name + status -->
                <div class="flex items-center gap-2 mb-1">
                  <span class="text-sm font-semibold text-n-slate-12 truncate">
                    {{ webhook.name }}
                  </span>
                  <span
                    class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-medium shrink-0"
                    :class="
                      webhook.active
                        ? 'bg-success-subtle text-success'
                        : 'bg-n-alpha-2 text-n-slate-10'
                    "
                  >
                    <span
                      class="size-1.5 rounded-full"
                      :class="webhook.active ? 'bg-success' : 'bg-n-slate-9'"
                    />
                    {{
                      webhook.active
                        ? $t('PIPELINE_WEBHOOK.STATUS.ACTIVE')
                        : $t('PIPELINE_WEBHOOK.STATUS.INACTIVE')
                    }}
                  </span>
                </div>

                <!-- URL -->
                <p class="text-xs text-n-slate-10 break-all font-mono mb-2.5">
                  {{ webhook.url }}
                </p>

                <!-- Event badges -->
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="event in webhook.events"
                    :key="event"
                    class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-medium"
                    :class="getEventMeta(event).classes"
                  >
                    <Icon
                      :icon="getEventMeta(event).icon"
                      class="size-3 shrink-0"
                    />
                    {{ getEventMeta(event).label }}
                  </span>
                </div>

                <!-- Last triggered -->
                <div
                  v-if="webhook.last_triggered_at"
                  class="flex items-center gap-1.5 mt-2.5 text-xs text-n-slate-10"
                >
                  <Icon icon="i-lucide-clock-3" class="size-3 shrink-0" />
                  <span>
                    {{ $t('PIPELINE_WEBHOOK.LAST_TRIGGERED') }}
                    {{ formatDate(webhook.last_triggered_at) }}
                  </span>
                  <span
                    v-if="webhook.last_status_code"
                    class="inline-flex items-center px-1.5 py-px rounded font-mono font-medium text-xs"
                    :class="
                      webhook.last_status_code < 300
                        ? 'bg-success-subtle text-success'
                        : 'bg-n-red-3 text-danger'
                    "
                  >
                    {{ webhook.last_status_code }}
                  </span>
                </div>
              </div>

              <!-- Actions — visible on hover -->
              <div class="flex shrink-0 items-center gap-0.5">
                <button
                  type="button"
                  class="flex size-11 items-center justify-center rounded-lg text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  :aria-label="$t('PIPELINE_WEBHOOK.ACTIONS.TEST')"
                  :disabled="
                    testingId !== null || deleteOperationToken !== null
                  "
                  @click="handleTest(webhook)"
                >
                  <Icon
                    :icon="
                      testingId === webhook.id
                        ? 'i-lucide-loader-circle'
                        : 'i-lucide-send'
                    "
                    class="size-3.5"
                    :class="{
                      'motion-safe:animate-spin': testingId === webhook.id,
                    }"
                  />
                </button>
                <button
                  type="button"
                  class="flex size-11 items-center justify-center rounded-lg text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  :aria-label="$t('PIPELINE_WEBHOOK.ACTIONS.EDIT')"
                  :disabled="
                    testingId !== null || deleteOperationToken !== null
                  "
                  @click="openEditModal(webhook)"
                >
                  <Icon icon="i-lucide-pencil" class="size-3.5" />
                </button>
                <button
                  type="button"
                  class="flex size-11 items-center justify-center rounded-lg text-n-slate-10 hover:text-danger hover:bg-n-red-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  :aria-label="$t('PIPELINE_WEBHOOK.ACTIONS.DELETE')"
                  :disabled="
                    testingId !== null || deleteOperationToken !== null
                  "
                  @click="handleDelete(webhook)"
                >
                  <Icon
                    :icon="
                      deletingId === webhook.id
                        ? 'i-lucide-loader-circle'
                        : 'i-lucide-trash-2'
                    "
                    class="size-3.5"
                    :class="{
                      'motion-safe:animate-spin': deletingId === webhook.id,
                    }"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <PipelineWebhookModal
      v-if="showModal"
      :webhook="selectedWebhook"
      :pipelines="pipelines"
      @close="closeModal"
      @saved="handleSaved"
    />

    <ConfirmModal
      ref="deleteConfirmation"
      :title="$t('PIPELINE_WEBHOOK.DELETE_TITLE')"
      :description="
        $t('PIPELINE_WEBHOOK.DELETE_CONFIRM', {
          name: pendingDelete?.name,
        })
      "
      :confirm-label="$t('PIPELINE_WEBHOOK.ACTIONS.DELETE')"
      :cancel-label="$t('PIPELINE_WEBHOOK.FORM.CANCEL')"
    />
  </div>
</template>
